/**
 * Security utilities for the application
 */

const isDevelopment = process.env.NODE_ENV === 'development';

// Generate a nonce for CSP inline scripts
export const generateNonce = () => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

// Validate Subresource Integrity (SRI) for external resources
export const validateSRI = (resourceUrl, expectedHash, algorithm = 'sha256') => {
  return new Promise((resolve, reject) => {
    fetch(resourceUrl)
      .then((response) => response.arrayBuffer())
      .then((buffer) => {
        // Convert ArrayBuffer to base64
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        const base64 = btoa(binary);

        // Calculate hash (this is a simplified version - in production, use a proper crypto library)
        // For now, we'll just validate that the resource loaded successfully
        resolve(true);
      })
      .catch((error) => {
        console.error('SRI validation failed:', error);
        reject(error);
      });
  });
};

// Sanitize user input to prevent XSS
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;

  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

// Validate URLs to prevent open redirects
export const validateUrl = (url) => {
  try {
    const urlObj = new URL(url);

    // In development, allow localhost and HTTP
    if (isDevelopment) {
      return true;
    }

    // In production, only allow HTTPS URLs
    if (urlObj.protocol !== 'https:') {
      return false;
    }

    // Add additional validation as needed
    return true;
  } catch {
    return false;
  }
};

// Check if the current page is loaded over HTTPS
export const isSecureContext = () => {
  return window.isSecureContext || window.location.protocol === 'https:';
};

// Log security events
export const logSecurityEvent = (event, details) => {
  if (isDevelopment) {
    console.warn(`Security Event: ${event}`, details);
  }
  // In production, send this to a security monitoring service
  // Example: sendToSecurityService(event, details);
};

// Validate file uploads
export const validateFileUpload = (file, allowedTypes = [], maxSize = 5 * 1024 * 1024) => {
  if (!file) return { valid: false, error: 'No file provided' };

  if (file.size > maxSize) {
    return { valid: false, error: 'File size exceeds maximum allowed size' };
  }

  if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
    return { valid: false, error: 'File type not allowed' };
  }

  return { valid: true };
};

// Prevent clickjacking
export const preventClickjacking = () => {
  if (window.self !== window.top) {
    window.top.location = window.self.location;
  }
};

// Test localhost connectivity (for development)
export const testLocalhostConnection = async (port = 5000) => {
  if (!isDevelopment) {
    return { success: false, message: 'Only available in development mode' };
  }

  try {
    const response = await fetch(`http://localhost:${port}/health`, {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      return { success: true, message: `Successfully connected to localhost:${port}` };
    } else {
      return { success: false, message: `Server responded with status: ${response.status}` };
    }
  } catch (error) {
    return { success: false, message: `Connection failed: ${error.message}` };
  }
};

// Initialize security measures
export const initializeSecurity = () => {
  // Prevent clickjacking
  preventClickjacking();

  // Log security context (only in development)
  if (isDevelopment && !isSecureContext()) {
    logSecurityEvent('insecure-context', { url: window.location.href });
  }

  // Add security event listeners
  document.addEventListener('securitypolicyviolation', (event) => {
    logSecurityEvent('csp-violation', {
      violatedDirective: event.violatedDirective,
      blockedURI: event.blockedURI,
      sourceFile: event.sourceFile,
      lineNumber: event.lineNumber,
    });
  });

  // Test localhost connection in development
  if (isDevelopment) {
    // Test connection after a short delay to ensure app is loaded
    setTimeout(async () => {
      const result = await testLocalhostConnection();
      if (result.success) {
        console.log('✅ Security: Localhost connection test passed');
      } else {
        console.warn('⚠️ Security: Localhost connection test failed -', result.message);
      }
    }, 2000);
  }
};
